import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import AppContext from "../context/AppProvider";
import {
  todayStr,
  getDaysInMonth,
  getFirstDayOfMonth,
  getDateStr,
  MONTH_NAMES,
  DAY_NAMES,
} from "../lib/dateUtils";
import { calcStreak } from "../lib/habitUtils";
import { BsGearFill } from "react-icons/bs";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";

const CalendarPage = () => {
  const { settings, themes, habits, addHabit } = useContext(AppContext);
  const navigate = useNavigate();
  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [newHabit, setNewHabit] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);

  const IconComponent = themes[settings.theme];
  const todayDateStr = todayStr();

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
  };

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = newHabit.trim();
    // Easter egg — handles iOS Unicode apostrophe variation
    const idx = 8;
    if (trimmed.slice(0, idx - 1) + trimmed.slice(idx) === "Larissas secret") {
      alert("I love you, beautiful. 😘");
      setNewHabit("");
      return;
    }
    if (!trimmed) return;
    addHabit(trimmed);
    setNewHabit("");
    setShowAddForm(false);
  };

  return (
    <div className="page">
      <div className="header">
        <IconComponent className="header-icon" style={{ color: settings.color }} />
        <h1 className="header-title">Larissa's Habit Tracker</h1>
        <BsGearFill
          className="header-gear"
          onClick={() => navigate("/settings")}
          style={{ color: settings.color }}
        />
      </div>

      <div className="month-nav">
        <button className="nav-btn" onClick={prevMonth}>
          <IoChevronBack />
        </button>
        <h2 className="month-label">
          {MONTH_NAMES[viewMonth]} {viewYear}
        </h2>
        <button className="nav-btn" onClick={nextMonth}>
          <IoChevronForward />
        </button>
      </div>

      <div className="calendar-grid">
        {DAY_NAMES.map((d) => (
          <div key={d} className="day-header">{d}</div>
        ))}
        {Array.from({ length: firstDay }).map((_, i) => (
          <div key={`e${i}`} className="day-cell empty" />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const dayNum = i + 1;
          const dateStr = getDateStr(viewYear, viewMonth, dayNum);
          const isToday = dateStr === todayDateStr;
          const doneDots = habits
            .filter((h) => h.entries[dateStr] === "done")
            .map((h) => h.color);
          return (
            <div
              key={dayNum}
              className="day-cell"
              style={isToday ? { boxShadow: `0 0 0 2px ${settings.color}` } : {}}
            >
              <span
                className="day-num"
                style={isToday ? { color: settings.color, fontWeight: 700 } : {}}
              >
                {dayNum}
              </span>
              <div className="dot-row">
                {doneDots.map((color, idx) => (
                  <span key={idx} className="dot" style={{ backgroundColor: color }} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="habits-section">
        <h3 className="section-title">My Habits</h3>
        {habits.length === 0 && (
          <p className="empty-msg">No habits yet. Add one below!</p>
        )}
        {habits.map((habit) => {
          const todayEntry = habit.entries[todayDateStr];
          const streak = calcStreak(habit);
          return (
            <div
              key={habit.id}
              className="habit-card"
              onClick={() => navigate(`/habit/${habit.id}`)}
            >
              <div className="habit-card-left">
                <span className="habit-dot" style={{ backgroundColor: habit.color }} />
                <span className="habit-name">{habit.name}</span>
              </div>
              <div className="habit-card-right">
                {streak > 0 && (
                  <span className="streak-badge">🔥 {streak}</span>
                )}
                <span
                  className={`status-pill ${
                    todayEntry === "done"
                      ? "done"
                      : todayEntry === "missed"
                      ? "missed"
                      : "unmarked"
                  }`}
                >
                  {todayEntry === "done" ? "✓" : todayEntry === "missed" ? "✗" : "—"}
                </span>
                <span className="chevron">›</span>
              </div>
            </div>
          );
        })}

        {showAddForm ? (
          <form className="add-form" onSubmit={handleSubmit}>
            <input
              autoFocus
              className="add-input"
              type="text"
              placeholder="Habit name..."
              value={newHabit}
              onChange={(e) => setNewHabit(e.target.value)}
            />
            <div className="add-form-btns">
              <button
                type="submit"
                className="btn-primary"
                style={{ backgroundColor: settings.color }}
              >
                Add
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => { setShowAddForm(false); setNewHabit(""); }}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <button
            className="add-habit-btn"
            onClick={() => setShowAddForm(true)}
            style={{ color: settings.color, borderColor: settings.color }}
          >
            + Add Habit
          </button>
        )}
      </div>
    </div>
  );
};

export default CalendarPage;
